import { useCallback, useMemo, useState } from 'react';
import { generateDraftSequence, getPhaseName } from './draftSequence';

const makeEmptyPick = () => ({
  champion: null,
  spell1: null,
  spell2: null,
  displayName: '',
  isActive: false,
});

const makeEmptyBan = () => ({
  champion: null,
  isActive: false,
});

const buildInitialTeam = () => ({
  picks: Array.from({ length: 5 }, makeEmptyPick),
  bans: Array.from({ length: 5 }, makeEmptyBan),
  isActive: false,
});

const EMPTY_SET = new Set();

// 教练模式 BP 状态机：
// - sequence 由 firstSide 决定（'blue' 蓝先手 / 'red' 红先手），仅本局开始前可切换
// - externalLockedIds 来自 useCoachBO 的全局历史 picks（Fearless 模式）
//   - 锁定的英雄会被并入 usedChampionIds，使 grid 无法再点
// - pickChampion(c) 把当前步替换/确认到对应槽位，并 stepIndex++
const useCoachDraft = ({
  firstSide = 'blue',
  externalLockedIds = EMPTY_SET,
} = {}) => {
  const [stepIndex, setStepIndex] = useState(0);
  const [blueTeam, setBlueTeam] = useState(buildInitialTeam);
  const [redTeam, setRedTeam] = useState(buildInitialTeam);
  // 战术摇摆选中：{ team: 'blue'|'red', slot: 0..4 } | null
  const [swapSelection, setSwapSelection] = useState(null);

  const sequence = useMemo(() => generateDraftSequence(firstSide), [firstSide]);

  const currentStep = stepIndex < sequence.length ? sequence[stepIndex] : null;
  const isFinished = currentStep === null;

  // 已用英雄 = 本局所有 ban/pick + 跨局外部锁
  const usedChampionIds = useMemo(() => {
    const ids = new Set(externalLockedIds);
    [blueTeam, redTeam].forEach((team) => {
      team.picks.forEach((p) => p.champion && ids.add(p.champion.id));
      team.bans.forEach((b) => b.champion && ids.add(b.champion.id));
    });
    return ids;
  }, [blueTeam, redTeam, externalLockedIds]);

  // 把高亮 isActive 写到当前步对应的槽位
  const applyActiveFlag = useCallback((team, step, side) => {
    if (!step || step.team !== side) {
      return {
        ...team,
        picks: team.picks.map((p) => ({ ...p, isActive: false })),
        bans: team.bans.map((b) => ({ ...b, isActive: false })),
        isActive: false,
      };
    }

    const picks = team.picks.map((p, idx) => ({
      ...p,
      isActive: step.type === 'pick' && idx === step.slot,
    }));
    const bans = team.bans.map((b, idx) => ({
      ...b,
      isActive: step.type === 'ban' && idx === step.slot,
    }));

    return { ...team, picks, bans, isActive: true };
  }, []);

  const blueRendered = useMemo(
    () => applyActiveFlag(blueTeam, currentStep, 'blue'),
    [blueTeam, currentStep, applyActiveFlag],
  );
  const redRendered = useMemo(
    () => applyActiveFlag(redTeam, currentStep, 'red'),
    [redTeam, currentStep, applyActiveFlag],
  );

  const pickChampion = useCallback(
    (champion) => {
      if (!currentStep || !champion) return;
      if (usedChampionIds.has(champion.id)) return;

      const setter = currentStep.team === 'blue' ? setBlueTeam : setRedTeam;
      setter((team) => {
        if (currentStep.type === 'ban') {
          const bans = team.bans.slice();
          bans[currentStep.slot] = { champion, isActive: false };
          return { ...team, bans };
        }
        const picks = team.picks.slice();
        picks[currentStep.slot] = {
          ...picks[currentStep.slot],
          champion,
          // 教练模式没有真实玩家名，复用 displayName 槽位展示英雄中文名
          displayName: champion.name,
          isActive: false,
        };
        return { ...team, picks };
      });

      setStepIndex((n) => n + 1);
    },
    [currentStep, usedChampionIds],
  );

  const undo = useCallback(() => {
    if (stepIndex === 0) return;
    const prevIndex = stepIndex - 1;
    const prevStep = sequence[prevIndex];
    const setter = prevStep.team === 'blue' ? setBlueTeam : setRedTeam;
    setter((team) => {
      if (prevStep.type === 'ban') {
        const bans = team.bans.slice();
        bans[prevStep.slot] = { champion: null, isActive: false };
        return { ...team, bans };
      }
      const picks = team.picks.slice();
      picks[prevStep.slot] = {
        ...picks[prevStep.slot],
        champion: null,
        displayName: '',
        isActive: false,
      };
      return { ...team, picks };
    });
    setStepIndex(prevIndex);
  }, [stepIndex, sequence]);

  const reset = useCallback(() => {
    setStepIndex(0);
    setBlueTeam(buildInitialTeam());
    setRedTeam(buildInitialTeam());
    setSwapSelection(null);
  }, []);

  // ========== 战术摇摆 / 位置互换 ==========
  // swapEnabled：仅当整局 draft 完成（20 步全部走完）才允许摇摆
  const swapEnabled = isFinished;

  // 直接对调两个槽位上的整对 pick 对象（保留 isActive、displayName 等）
  // applyActiveFlag 会重算 isActive，所以即使 swap 也不会让 isActive 错位
  const swapPicks = useCallback((team, slotA, slotB) => {
    if (slotA === slotB) return;
    const setter = team === 'blue' ? setBlueTeam : setRedTeam;
    setter((t) => {
      const picks = t.picks.slice();
      const tmp = picks[slotA];
      picks[slotA] = picks[slotB];
      picks[slotB] = tmp;
      return { ...t, picks };
    });
  }, []);

  // 教练点 pick 卡片：根据 swap 状态机派发动作
  const clickPickForSwap = useCallback((team, slot, hasChampion) => {
    if (!swapEnabled) return; // 整局没完成前禁止摇摆
    if (!hasChampion) {
      // 点空格 / 占位卡：清掉当前选中
      setSwapSelection(null);
      return;
    }
    if (!swapSelection) {
      // 首次选中
      setSwapSelection({ team, slot });
      return;
    }
    if (swapSelection.team !== team) {
      // 跨队点击：按约定完全忽略，保留原选中
      return;
    }
    if (swapSelection.slot === slot) {
      // 重复点击同一格：取消选中
      setSwapSelection(null);
      return;
    }
    // 合法摇摆：执行互换并清空选中
    swapPicks(team, swapSelection.slot, slot);
    setSwapSelection(null);
  }, [swapEnabled, swapSelection, swapPicks]);

  const cancelSwap = useCallback(() => setSwapSelection(null), []);

  // 给 BO 用：抽取当前局的"已选英雄"快照（带 side 与所属 game 维度，由调用方补全）
  const snapshotPicks = useCallback(() => ({
    bluePicks: blueTeam.picks
      .map((p) => p.champion)
      .filter((c) => c && c.id),
    redPicks: redTeam.picks
      .map((p) => p.champion)
      .filter((c) => c && c.id),
    blueBans: blueTeam.bans
      .map((b) => b.champion)
      .filter((c) => c && c.id),
    redBans: redTeam.bans
      .map((b) => b.champion)
      .filter((c) => c && c.id),
  }), [blueTeam, redTeam]);

  return {
    stepIndex,
    currentStep,
    isFinished,
    phaseName: getPhaseName(stepIndex),
    blueTeam: blueRendered,
    redTeam: redRendered,
    usedChampionIds,
    pickChampion,
    undo,
    reset,
    snapshotPicks,
    // 战术摇摆相关
    swapEnabled,
    swapSelection,
    clickPickForSwap,
    cancelSwap,
  };
};

export default useCoachDraft;
